import React from 'react';
import styled from 'styled-components';
import { HUB_GUTTER_CSS } from './hubType';
import { theme } from '../../theme';

const Disclosure = styled.p<{ $wideGutter?: boolean }>`
  ${HUB_GUTTER_CSS}
  margin-top: 0; margin-bottom: 0; padding-top: 16px; padding-bottom: 16px;
  background: white; color: #6B6B6B; font-size: 13px; line-height: 1.5;
`;
export interface HubDisclosureProps {
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to `false` so
   * every existing caller keeps today's capped layout when the hub gate is off.
   */
  wideGutterEnabled?: boolean;
}
export function HubDisclosure({ wideGutterEnabled = false }: HubDisclosureProps = {}) {
  return (
    <Disclosure $wideGutter={wideGutterEnabled}>
      Some links earn us a small commission at no cost to you.
    </Disclosure>
  );
}

/**
 * The system primary button — PRD Visual style, "Dark buttons ('Add someone',
 * 'Show 5 gifts'): 14–15px semibold, fully rounded, near-black #2A211C, white
 * text". Near-black rather than clay is deliberate and is canvas discrepancy 3:
 * the hub's `Add someone` is near-black while the You tab's `Log in or create
 * account` is rust, so near-black is the SYSTEM primary and rust is reserved
 * for auth surfaces (which is where today's site already uses it). A hub CTA
 * that reaches for `theme.color.clay` is making itself look like a sign-in.
 *
 * Lives here, beside the disclosure and the bands, because three separate
 * tickets need the same pill (`Add someone` on the hub's people card, `Add
 * someone` on the Calendar reminder card, `Add your first person` on the
 * People tab) and the PRD gives it one spec.
 *
 * THE-1092: sized up from the PRD's 14-15px/12px×22px to the bigger
 * before/after mock Kate approved for THE-1081 (16px/14px×28px) — THE-1081
 * shipped the PRD's original smaller size, which read as undersized next to
 * the 56px hero headline it ships beside.
 */
/** The near-black of the PRD's Visual style row, as one value — the `ink`
 *  theme token (THE-1300). */
export const HUB_PRIMARY_BG = theme.color.ink;

/* `data-hub-primary` is the TESTABLE HANDLE for "this is the near-black system
   primary", and it exists because the colour itself cannot be asserted in
   jsdom. jsdom's getComputedStyle does not evaluate pseudo-class selectors: it
   applies the `&:hover` declaration unconditionally, so reading
   background-color off a resting instance returns the HOVER near-black and a
   base-colour assertion can never pass. (It also passes its own negation,
   which is how a colour test here looks green while proving nothing.)

   So tests assert the ATTRIBUTE — i.e. that a CTA is this component rather
   than the clay `Button` — which is the regression that actually matters:
   near-black vs rust is canvas discrepancy 3, and the way it would break is
   someone dropping the `ctaSlot`, not someone editing the hex. The hex has one
   home, above. */
const PrimaryButton = styled.button`
  border: 0; border-radius: 999px; padding: 14px 28px;
  background-color: ${HUB_PRIMARY_BG}; color: white;
  font: inherit; font-size: 16px; font-weight: 600; line-height: 1.2;
  cursor: pointer; white-space: nowrap;
  &:hover { background-color: #3A2E27; }
`;

/* A thin wrapper rather than `.attrs({ 'data-hub-primary': '' })`, because
   styled-components' `Attrs` type rejects an arbitrary `data-*` key in an
   object literal — React only permits those in JSX. So the marker is set in
   JSX, where it type-checks honestly, instead of being cast past the checker. */
export const HubPrimaryButton: React.FC<React.ComponentPropsWithoutRef<'button'>> =
  (props) => <PrimaryButton data-hub-primary="" {...props} />;

/**
 * THE-1081: the same one primary button, inverted for a dark photo
 * background — "one primary button style everywhere: dark on light
 * backgrounds, white on the photo banner." Same shape/padding/weight as
 * `PrimaryButton` above; only the fill and text colour flip, so the two
 * read as the same button in two contexts rather than a second style.
 */
const PrimaryButtonOnPhoto = styled.button`
  border: 0; border-radius: 999px; padding: 14px 28px;
  background-color: #ffffff; color: ${HUB_PRIMARY_BG};
  font: inherit; font-size: 16px; font-weight: 600; line-height: 1.2;
  cursor: pointer; white-space: nowrap;
  &:hover { background-color: #F2EDE6; }
`;

export const HubPrimaryButtonOnPhoto: React.FC<React.ComponentPropsWithoutRef<'button'>> =
  (props) => <PrimaryButtonOnPhoto data-hub-primary-on-photo="" {...props} />;

/** Bands own the viewport width; only their contents take the page gutter.
 * Mount outside gutter-constrained content, as GiftGuideHubView does.
 *
 * THE-1374: the plum tone reads the shared `theme.color.plum` token instead
 * of its own literal, so this band and the board's "Dive deeper" block
 * (DiveDeeperBlock.tsx) share one named colour and cannot drift apart again —
 * previously each hard-coded its own hex (#3B2436 here, #5B3A5E there) and
 * the board standard said they were deliberately separate. */
export const HubBand = styled.section<{ $tone: 'plum' | 'mocha' }>`
  width: 100%; padding: 24px 0;
  background: ${({ $tone }) => $tone === 'plum' ? theme.color.plum : theme.color.cream};
  color: ${({ $tone }) => $tone === 'plum' ? 'white' : '#38322E'};
`;
export const HubBandContent = styled.div<{ $wideGutter?: boolean }>`${HUB_GUTTER_CSS}`;
